import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  CardContent, 
  Typography, 
  Stack, 
  IconButton, 
  FormControlLabel, 
  Checkbox, 
  Grid, 
  useMediaQuery, 
  Button, 
  Menu, 
  MenuItem, 
  ListItemIcon,
  Divider,
  Box,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { useOrganizationContext } from '../../../context/organization_context/OrganizationContext';
import { useLoadingContext } from '../../../context/loading/LoadingContext';
import { useToast, ToastType } from '../../../context/toast/ToastContext';
import DesktopAppSidebar from '../../../components/navbar/DesktopAppSidebar';
import ConfirmDeleteModal from '../../../components/modals/ConfirmDeleteModal';
import { updateRolePermissions, deleteRole } from '../../../repository/OrganizationDAO';
import { Loading } from '../../../components/loading/Loading';
import { Modules } from '../../../utils/permissions/Modules';
import { PermissionTypes } from '../../../utils/permissions/PermissionTypes';
import usePermissions from '../../../utils/permissions/UsePermissions';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../resources/strings/StringKeys';
import { Delete, MoreVert, InfoOutlined, ExpandMore } from '@mui/icons-material';
import { UserRole } from '../../../utils/enums/UserRoles';
import { handleMenuClick, handleMenuClose } from '../../../components/utils/MenuHandlers';

// Define module groups for better organization
const MODULE_GROUPS = {
  employees: ['employees', 'departments', 'performance_reviews'],
  inventory: ['inventory', 'inventory_orders', 'suppliers', 'unit_of_measure'],
  invoices: ['invoices', 'invoice_dashboard', 'products', 'invoice_settings'],
  time_tracking: ['time_tracking', 'time_clock', 'review_time_clocks']
};

// Get the group a module belongs to
const getModuleGroup = (module) => {
  for (const [group, modules] of Object.entries(MODULE_GROUPS)) {
    if (modules.includes(module)) {
      return group;
    }
  }
  return null; // No group
};

const RoleDetail = () => {
  const { roleId } = useParams();
  const { organization } = useOrganizationContext();
  const { setLoadingData } = useLoadingContext();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [role, setRole] = useState(null);
  const { hasPermission } = usePermissions();
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width:600px)');
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({
    BASIC: true,
    EMPLOYEES: false,
    INVENTORY: false,
    INVOICES: false,
    TIME_TRACKING: false
  });

  useEffect(() => {
    if (organization && organization.roles && roleId) {
      const roleData = organization.roles[roleId];
      setRole(roleData);
    }
  }, [organization, roleId]);

  const handlePermissionChange = async (module, permission) => {
    if (module === 'organization' && permission === 'read') return;

    const updatedRole = {
      ...role,
      [module]: {
        ...role[module],
        [permission]: !role[module][permission]
      }
    };
    
    if (module === 'organization') {
      updatedRole.organization.read = true;
    }
    
    setRole(updatedRole);
    setLoadingData(true);

    try {
      const result = await updateRolePermissions(organization.id, roleId, updatedRole);
      if (result.success) {
        showToast(t(StringKeys.SUCCESS), ToastType.SUCCESS);
      } else {
        showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
      }
    } catch (error) {
      console.error("Unexpected error:", error);
      showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
    } finally {
      setLoadingData(false);
    }
  };

  const handleSelectAllChange = async (module) => {
    const allSelected = Object.keys(role[module]).every(permission => role[module][permission]);
    const updatedPermissions = Object.keys(role[module]).reduce((acc, permission) => {
      if (module === 'organization' && permission === 'read') {
        acc[permission] = true;
      } else {
        acc[permission] = !allSelected;
      }
      return acc;
    }, {});

    const updatedRole = {
      ...role,
      [module]: updatedPermissions
    };

    setRole(updatedRole);
    setLoadingData(true);

    try {
      const result = await updateRolePermissions(organization.id, roleId, updatedRole);
      if (result.success) {
        showToast(t(StringKeys.SUCCESSFULLY_UPDATED_ROLE_PERMISSIONS), ToastType.SUCCESS);
      } else {
        showToast(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN, ToastType.DANGER);
      }
    } catch (error) {
      console.error("Unexpected error:", error);
      showToast(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN, ToastType.DANGER);
    } finally {
      setLoadingData(false);
    }
  };

  const handleDelete = async () => {
    setLoadingData(true);
    try {
      const result = await deleteRole(organization.id, roleId);
      if (result.success) {
        showToast(t(StringKeys.SUCCESSFULLY_DELETED_ROLE), ToastType.SUCCESS);
        navigate('/roles', { replace: true });
      } else {
        showToast(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN, ToastType.DANGER);
      }
    } catch (error) {
      showToast(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN, ToastType.DANGER);
    } finally {
      setLoadingData(false);
    }
  };

  const handleGroupExpand = (group) => {
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  const permissionOrder = [
    'read', 
    'write', 
    'send_invoice',
    'modify_review_templates',
    'read_all_reviews',
    'read_and_write_all_reviews',
    'read_and_write_own_review',
    'read_and_write_reviews_for_managed_employees',
    'read_all_time_clocks',
    'read_and_write_all_time_clocks',
    'read_and_write_time_clocks_for_managed_employees',
  ];

  if (!role) return <DesktopAppSidebar content={<Loading />} />;

  // Organize modules by group or individual
  const modulesByGroup = {};
  
  // Sort all modules alphabetically first
  const allModulesSorted = Object.keys(role).sort();
  
  // Process modules by group
  allModulesSorted.forEach(module => {
    const group = getModuleGroup(module);
    
    if (group) {
      if (!modulesByGroup[group]) modulesByGroup[group] = [];
      modulesByGroup[group].push(module);
    } else {
      // Individual modules get their own "group"
      modulesByGroup[module] = [module];
    }
  });

  return (
    <DesktopAppSidebar
      content={
        <Stack spacing={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="h5">{roleId}</Typography>

            {!isMobile && 
              hasPermission(Modules.ROLES, PermissionTypes.WRITE) &&
                roleId !== UserRole.Owner && (
                  <Button 
                    variant="contained" 
                    startIcon={<Delete />} 
                    onClick={() => setConfirmOpen(true)}
                  >
                    {t(StringKeys.DELETE)}
                  </Button>
            )}
            {isMobile && hasPermission(Modules.ROLES, PermissionTypes.WRITE) && roleId !== UserRole.Owner && (
              <>
                <IconButton onClick={(event) => handleMenuClick(event, setAnchorEl)}>
                  <MoreVert />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => handleMenuClose(setAnchorEl)}
                >
                  <MenuItem onClick={() => { setConfirmOpen(true); handleMenuClose(setAnchorEl); }}>
                    <ListItemIcon>
                      <Delete fontSize="small" />
                    </ListItemIcon>
                    {t(StringKeys.DELETE)}
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>

          {hasPermission(Modules.ROLES, PermissionTypes.WRITE) && (
            <Stack spacing={2}>
              {/* Render each group/module */}
              {Object.keys(modulesByGroup).map(groupOrModule => (
                <Card key={groupOrModule} variant="outlined">
                  <CardContent>
                    {/* If this is a group (has multiple modules) */}
                    {Array.isArray(modulesByGroup[groupOrModule]) && modulesByGroup[groupOrModule].length > 1 ? (
                      <>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                          {t(groupOrModule)}
                        </Typography>
                        <Stack spacing={2}>
                          {modulesByGroup[groupOrModule].map(module => (
                            <Box key={module}>
                              <Box display="flex" alignItems="center" mb={1}>
                                <Box display="flex" alignItems="center">
                                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                    {t(module)}
                                  </Typography>
                                  {module === 'roles' && roleId === UserRole.Owner && (
                                    <Tooltip title={t(StringKeys.OWNER_IS_REQUIRED_TO_MAINTAIN_FULL_ROLE_PERMISISONS_)}>
                                      <InfoOutlined 
                                        fontSize="small" 
                                        sx={{ 
                                          ml: 0.5,
                                          color: 'info.main',
                                          cursor: 'help'
                                        }} 
                                      />
                                    </Tooltip>
                                  )}
                                </Box>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={Object.keys(role[module]).every(permission => role[module][permission])}
                                      onChange={() => handleSelectAllChange(module)}
                                      disabled={module === 'roles' && roleId === UserRole.Owner}
                                    />
                                  }
                                  label={<Typography variant="body2">{t(StringKeys.SELECT_ALL_PERMISSIONS)}</Typography>}
                                  sx={{ ml: 2 }}
                                />
                              </Box>
                              
                              <Grid container spacing={1}>
                                {permissionOrder
                                  .filter(permission => role[module].hasOwnProperty(permission))
                                  .map(permission => (
                                    <Grid item xs={6} sm={4} md={3} key={permission}>
                                      <Box display="flex" alignItems="center">
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              size="small"
                                              checked={role[module][permission]}
                                              onChange={() => handlePermissionChange(module, permission)}
                                              disabled={
                                                (module === 'roles' && roleId === UserRole.Owner) ||
                                                (module === 'organization' && permission === 'read')
                                              }
                                            />
                                          }
                                          label={
                                            <Typography 
                                              variant="body2" 
                                              sx={{ 
                                                fontSize: '0.875rem',
                                                color: module === 'organization' && permission === 'read' ? 'text.disabled' : 'inherit'
                                              }}
                                            >
                                              {t(permission)}
                                            </Typography>
                                          }
                                          sx={{ 
                                            m: 0,
                                            '& .MuiFormControlLabel-label': { 
                                              whiteSpace: 'nowrap',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis'
                                            }
                                          }}
                                        />
                                        {(module === 'organization' && permission === 'read') && (
                                          <Tooltip title={t(StringKeys.ORGANIZATION_READ_PERMISSION_IS_REQUIRED_FOR_ALL_USERS)}>
                                            <InfoOutlined 
                                              fontSize="small" 
                                              sx={{ 
                                                ml: 0.5,
                                                color: 'info.main',
                                                cursor: 'help'
                                              }} 
                                            />
                                          </Tooltip>
                                        )}
                                      </Box>
                                    </Grid>
                                  ))}
                              </Grid>
                              {module !== modulesByGroup[groupOrModule][modulesByGroup[groupOrModule].length - 1] && (
                                <Divider sx={{ my: 2 }} />
                              )}
                            </Box>
                          ))}
                        </Stack>
                      </>
                    ) : (
                      // Individual module (not in a group or a group with only one module)
                      <>
                        <Box display="flex" alignItems="center" mb={1}>
                          <Box display="flex" alignItems="center">
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                              {t(modulesByGroup[groupOrModule][0])}
                            </Typography>
                            {modulesByGroup[groupOrModule][0] === 'roles' && roleId === UserRole.Owner && (
                              <Tooltip title={t(StringKeys.OWNER_IS_REQUIRED_TO_MAINTAIN_FULL_ROLE_PERMISISONS_)}>
                                <InfoOutlined 
                                  fontSize="small" 
                                  sx={{ 
                                    ml: 0.5,
                                    color: 'info.main',
                                    cursor: 'help'
                                  }} 
                                />
                              </Tooltip>
                            )}
                          </Box>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={Object.keys(role[modulesByGroup[groupOrModule][0]]).every(permission => role[modulesByGroup[groupOrModule][0]][permission])}
                                onChange={() => handleSelectAllChange(modulesByGroup[groupOrModule][0])}
                                disabled={modulesByGroup[groupOrModule][0] === 'roles' && roleId === UserRole.Owner}
                              />
                            }
                            label={<Typography variant="body2">{t(StringKeys.SELECT_ALL_PERMISSIONS)}</Typography>}
                            sx={{ ml: 2 }}
                          />
                        </Box>
                        
                        <Grid container spacing={1}>
                          {permissionOrder
                            .filter(permission => role[modulesByGroup[groupOrModule][0]].hasOwnProperty(permission))
                            .map(permission => (
                              <Grid item xs={6} sm={4} md={3} key={permission}>
                                <Box display="flex" alignItems="center">
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={role[modulesByGroup[groupOrModule][0]][permission]}
                                        onChange={() => handlePermissionChange(modulesByGroup[groupOrModule][0], permission)}
                                        disabled={
                                          (modulesByGroup[groupOrModule][0] === 'roles' && roleId === UserRole.Owner) ||
                                          (modulesByGroup[groupOrModule][0] === 'organization' && permission === 'read')
                                        }
                                      />
                                    }
                                    label={
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          fontSize: '0.875rem',
                                          color: modulesByGroup[groupOrModule][0] === 'organization' && permission === 'read' ? 'text.disabled' : 'inherit'
                                        }}
                                      >
                                        {t(permission)}
                                      </Typography>
                                    }
                                    sx={{ 
                                      m: 0,
                                      '& .MuiFormControlLabel-label': { 
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                      }
                                    }}
                                  />
                                  {(modulesByGroup[groupOrModule][0] === 'organization' && permission === 'read') && (
                                    <Tooltip title={t(StringKeys.ORGANIZATION_READ_PERMISSION_IS_REQUIRED_FOR_ALL_USERS)}>
                                      <InfoOutlined 
                                        fontSize="small" 
                                        sx={{ 
                                          ml: 0.5,
                                          color: 'info.main',
                                          cursor: 'help'
                                        }} 
                                      />
                                    </Tooltip>
                                  )}
                                </Box>
                              </Grid>
                            ))}
                        </Grid>
                      </>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}

          <ConfirmDeleteModal
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            onConfirm={() => { handleDelete(); setConfirmOpen(false); }}
            item={roleId}
          />
        </Stack>
      }
    />
  );
};

export default RoleDetail;